import { useEffect, useRef, useState } from 'react';
import { featureIsIncludedInCurrentFilter } from '../../helpers/filter';
import { Data } from '../../hooks/use-data2.hook';
import { GeoLevel, Indicator } from '../../types';
import { DownloadIcon } from '../Icons';
import { useTranslation } from 'react-i18next';
import { Feature, GeoJsonProperties, Geometry } from 'geojson';

function getFileName(
    indicator: Indicator,
    featureFilter: string[],
    language: string,
    extension: string
): string {
    const fileNameParts = [];

    if (indicator.category?.[language] ?? indicator.category?.['sv']) {
        fileNameParts.push(
            indicator.category?.[language] ?? indicator.category?.['sv']
        );
    }

    fileNameParts.push(
        (indicator.label[language] ?? indicator.label['sv'])
            .replaceAll(' ', '_')
            .replaceAll(',', '')
    );
    fileNameParts.push(
        (indicator.subLabel[language] ?? indicator.subLabel['sv'])
            .replaceAll(' ', '_')
            .replaceAll(',', '')
    );

    if (indicator.unitDisplay) {
        fileNameParts.push(indicator.unitDisplay);
    }

    if (featureFilter.length > 0) {
        fileNameParts.push(featureFilter.join('_'));
    }

    return `${fileNameParts.join('_')}.${extension}`;
}

async function triggerExportDownload(
    indicator: Indicator,
    geoLevel: GeoLevel,
    filteredFeatures: Feature<Geometry, GeoJsonProperties>[],
    format: 'csv' | 'xlsx',
    filename: string
) {
    const url = `${import.meta.env.VITE_API_URL}/api/v1/data/indicators/${geoLevel}/${indicator.id}/wide.${format}`;
    const geoIds = filteredFeatures.map((f) => f.properties?.id).filter(Boolean) as string[];

    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ geoIds }),
    });

    if (!response.ok) return;

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(objectUrl);
}

type Props = {
    indicator: Indicator;
    geoLevel: GeoLevel;
    featureFilter: string[];
    data: Pick<Data, 'points'>;
};

export function DownloadIndicatorButton(props: Props) {
    const { t, i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const dialogRef = useRef<HTMLDialogElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (isOpen && dialogRef.current) {
            dialogRef.current.showModal();
        }
    }, [isOpen]);

    function handleClose() {
        setIsOpen(false);
        triggerRef.current?.focus();
    }

    const filteredFeatures = props.data.points.filter((f) =>
        featureIsIncludedInCurrentFilter(f, props.featureFilter)
    );

    return (
        <>
            <button
                ref={triggerRef}
                onClick={() => setIsOpen(true)}
                className="btn btn-xs btn-circle btn-primary"
                aria-label={`${t('download')} ${props.indicator.label[i18n.language] ?? props.indicator.label['sv']}`}
            >
                <DownloadIcon fill="white" />
            </button>
            {isOpen && (
                <dialog
                    ref={dialogRef}
                    id={`${props.indicator.id}-download-modal`}
                    className="modal"
                    onClose={handleClose}
                >
                    <div className="modal-box w-fit">
                        <ul className="menu menu-vertical">
                            <li>
                                <button
                                    onClick={() => {
                                        triggerExportDownload(
                                            props.indicator,
                                            props.geoLevel,
                                            filteredFeatures,
                                            'csv',
                                            getFileName(props.indicator, props.featureFilter, i18n.language, 'csv')
                                        );
                                        dialogRef.current?.close();
                                    }}
                                >
                                    CSV (.csv)
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => {
                                        triggerExportDownload(
                                            props.indicator,
                                            props.geoLevel,
                                            filteredFeatures,
                                            'xlsx',
                                            getFileName(props.indicator, props.featureFilter, i18n.language, 'xlsx')
                                        );
                                        dialogRef.current?.close();
                                    }}
                                >
                                    Excel (.xlsx)
                                </button>
                            </li>
                        </ul>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button>close</button>
                    </form>
                </dialog>
            )}
        </>
    );
}
